// SPACE WARP: THE LOCKED PASS (w54 quest-sw, 2026-11-03; APP_SURFACE_PERF "Space warp", APP_PLAYER §VR "Space warp").
// spacewarp.js's depth reprojection gives EVERY pixel the motion of a static world point under the camera's motion.
// Everything that rides the rig or the head is not static: the HUD (compass + status line), the menu panels, the
// held weapon, the controllers / hand markers, the loading room. With the static MV the compositor slid them WITH
// the world in each synthesized frame and the next real frame put them back — "two pictures when moving"
// (headset, w54: the head-locked status bar stood still in every frame with the warp off, and jumped ±6 px in
// 140 of 259 frames with it on, while turning).
// The fix is Meta's own rule for anything that moves (cube-sea.js: clip(cur VP · cur model) − clip(prev VP · prev
// model)): after the reprojection, every visible mesh under the rig is drawn again into the motion vector + depth
// target with ITS OWN previous and current matrices (float64 here, the products uploaded — no world coordinate
// on the GPU). A head-locked panel gets MV 0, a hand its real motion.
//   createLockedPass(gl, renderer) -> { draw(root, cams, curVP, lastVP, cut, gain), dispose(), facts }
// RULES (generic, no list of objects):
// - drawn: a visible Mesh (not Instanced / Skinned / morphed, one material) under `root` whose layers the view
//   sees, with a non-interleaved float32 xyz position;
// - a material with a map is alpha-tested on the map (× opacity) at 0.5 — a panel's transparent canvas stays the
//   world's; a map three has not uploaded yet skips the mesh;
// - a translucent wash (transparent, no map, opacity < 0.5: the comfort vignette, the dizzy / underwater tints)
//   stays the world's: the world dominates its pixels; `userData.swSkip` opts any mesh out;
// - order: depth-tested meshes first, then the over-the-world ones (depthTest off: the HUD, panels) by renderOrder;
//   depth ALWAYS (the colour pass already resolved what is in front);
// - a new mesh / a cut (beam, recenter) has no previous: MV 0.
import * as THREE from 'three';

const VS = `#version 300 es
#extension GL_OVR_multiview2 : require
layout(num_views = 2) in;
in vec3 aPos;
in vec2 aUv;
uniform mat4 uCur[2];
uniform mat4 uPrev[2];
uniform mat3 uMapM;
out vec4 vC;
out vec4 vP;
out vec2 vUv;
void main() {
  int v = int(gl_ViewID_OVR);
  vC = uCur[v] * vec4(aPos, 1.0);
  vP = uPrev[v] * vec4(aPos, 1.0);
  vUv = (uMapM * vec3(aUv, 1.0)).xy;
  gl_Position = vC;
}`;
const FS = `#version 300 es
precision highp float;
uniform sampler2D uMap;
uniform float uAlpha;   // < 0: no map; else the material opacity (alpha test on map.a × it)
uniform float uGain;    // spacewarp.js SW_MV_GAIN (the same scale as the reprojection's MV)
in vec4 vC;
in vec4 vP;
in vec2 vUv;
layout(location = 0) out vec4 oMv;
void main() {
  if (uAlpha >= 0.0 && texture(uMap, vUv).a * uAlpha < 0.5) discard;
  oMv = vec4((vC.xyz / vC.w - vP.xyz / vP.w) * uGain, 0.0);
}`;

const VIEWS = 2;

export function createLockedPass(gl, renderer) {
  let p = null;                               // the program + locations
  const geos = new WeakMap();                 // geometry -> { vao, pos, uv, idx, verP, verU, verI, type, n }
  const prevW = new WeakMap();                // mesh -> { m: its matrixWorld, n: the pass it was drawn in }
  let pass = 0;
  const facts = { drawn: 0, skipped: 0, err: null };
  const cur = new Float32Array(16 * VIEWS), prev = new Float32Array(16 * VIEWS);
  const Mc = new THREE.Matrix4(), Mp = new THREE.Matrix4();
  const list = [];

  function program() {
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const prog = gl.createProgram(), vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
    gl.attachShader(prog, vs); gl.attachShader(prog, fs);
    gl.bindAttribLocation(prog, 0, 'aPos'); gl.bindAttribLocation(prog, 1, 'aUv');
    gl.linkProgram(prog);
    gl.deleteShader(vs); gl.deleteShader(fs);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      facts.err = (gl.getProgramInfoLog(prog) || 'link failed').slice(0, 200);
      gl.deleteProgram(prog);
      return null;
    }
    const u = (n) => gl.getUniformLocation(prog, n);
    return { prog, uCur: u('uCur[0]'), uPrev: u('uPrev[0]'), uMapM: u('uMapM'), uMap: u('uMap'), uAlpha: u('uAlpha'), uGain: u('uGain') };
  }

  const plain = (a, size) => a && !a.isInterleavedBufferAttribute && a.itemSize === size && a.array instanceof Float32Array;

  // our own buffers of a geometry (three's are not shared across programs here): re-uploaded on a version change
  function buffers(geo) {
    const pos = geo.attributes.position, uv = geo.attributes.uv, idx = geo.index;
    if (!plain(pos, 3)) return null;
    let b = geos.get(geo);
    if (!b) {
      b = { vao: gl.createVertexArray(), pos: gl.createBuffer(), uv: null, idx: null, verP: -1, verU: -1, verI: -1, type: 0 };
      geos.set(geo, b);
      geo.addEventListener('dispose', function gone() {
        geo.removeEventListener('dispose', gone);
        gl.deleteVertexArray(b.vao); gl.deleteBuffer(b.pos); if (b.uv) gl.deleteBuffer(b.uv); if (b.idx) gl.deleteBuffer(b.idx);
        geos.delete(geo);
      });
    }
    gl.bindVertexArray(b.vao);
    if (b.verP !== pos.version) {
      gl.bindBuffer(gl.ARRAY_BUFFER, b.pos); gl.bufferData(gl.ARRAY_BUFFER, pos.array, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      b.verP = pos.version;
    }
    if (plain(uv, 2)) {
      if (b.verU !== uv.version) {
        if (!b.uv) b.uv = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, b.uv); gl.bufferData(gl.ARRAY_BUFFER, uv.array, gl.STATIC_DRAW);
        gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 0, 0);
        b.verU = uv.version;
      }
    } else { gl.disableVertexAttribArray(1); gl.vertexAttrib2f(1, 0, 0); }
    if (idx) {
      if (b.verI !== idx.version) {
        if (!b.idx) b.idx = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, b.idx); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx.array, gl.STATIC_DRAW);
        b.type = idx.array instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
        b.verI = idx.version;
      }
    }
    return b;
  }

  function eligible(o, layers) {
    if (!o.isMesh || o.isInstancedMesh || o.isSkinnedMesh) return false;
    if (o.userData && o.userData.swSkip) return false;
    if (layers && !o.layers.test(layers)) return false;
    const m = o.material, g = o.geometry;
    if (!m || Array.isArray(m) || m.visible === false || m.colorWrite === false) return false;
    if (!g || !g.attributes || (g.morphAttributes && g.morphAttributes.position)) return false;
    if (m.transparent && !m.map && !(m.opacity >= 0.5)) return false;
    return true;
  }

  return {
    facts,
    // into the bound motion vector framebuffer (spacewarp.js draw, after the reprojection; viewport set there)
    draw(root, cams, curVP, lastVP, cut, gain = 1) {
      facts.drawn = 0; facts.skipped = 0;
      pass++;
      if (!root) return;
      list.length = 0;
      const layers = cams[0] && cams[0].layers;
      root.traverseVisible((o) => { if (o.isMesh) { if (eligible(o, layers)) list.push(o); else facts.skipped++; } });
      if (!list.length) return;
      if (!p) { p = program(); if (!p) return; }
      list.sort((a, b) => (a.material.depthTest === false) - (b.material.depthTest === false) || a.renderOrder - b.renderOrder);
      gl.useProgram(p.prog);
      gl.uniform1i(p.uMap, 0);
      gl.uniform1f(p.uGain, gain);
      gl.activeTexture(gl.TEXTURE0);
      for (const o of list) {
        const m = o.material;
        let alpha = -1;
        if (m.map) {
          const t = renderer.properties.get(m.map).__webglTexture;
          if (!t) { facts.skipped++; continue; }
          gl.bindTexture(gl.TEXTURE_2D, t);
          gl.uniformMatrix3fv(p.uMapM, false, m.map.matrix.elements);
          alpha = m.transparent ? m.opacity : 1;
        } else {
          gl.bindTexture(gl.TEXTURE_2D, null);
        }
        const b = buffers(o.geometry);
        if (!b) { facts.skipped++; continue; }
        const W = o.matrixWorld;
        let pw = prevW.get(o);
        const fresh = cut || !pw || pw.n !== pass - 1;     // new, or not drawn last pass (shown again): no previous
        if (!pw) { pw = { m: new THREE.Matrix4(), n: 0 }; prevW.set(o, pw); }
        for (let v = 0; v < VIEWS; v++) {
          Mc.multiplyMatrices(curVP[v], W);
          if (fresh) Mp.copy(Mc); else Mp.multiplyMatrices(lastVP[v], pw.m);
          cur.set(Mc.elements, 16 * v); prev.set(Mp.elements, 16 * v);
        }
        pw.m.copy(W); pw.n = pass;
        gl.uniformMatrix4fv(p.uCur, false, cur);
        gl.uniformMatrix4fv(p.uPrev, false, prev);
        gl.uniform1f(p.uAlpha, alpha);
        const g = o.geometry, r = g.drawRange;
        if (g.index) {
          const n = Math.min(g.index.count - r.start, r.count);
          if (n > 0) gl.drawElements(gl.TRIANGLES, n, b.type, r.start * (b.type === gl.UNSIGNED_INT ? 4 : 2));
        } else {
          const n = Math.min(g.attributes.position.count - r.start, r.count);
          if (n > 0) gl.drawArrays(gl.TRIANGLES, r.start, n);
        }
        facts.drawn++;
      }
      gl.bindVertexArray(null);
      gl.bindTexture(gl.TEXTURE_2D, null);
    },
    dispose() { if (p) { gl.deleteProgram(p.prog); p = null; } },
  };
}
